// SPDX-FileCopyrightText: Copyright 2025 New Vector Ltd.
// SPDX-FileCopyrightText: Copyright 2025, 2026 Element Creations Ltd.
//
// SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial

import { useIntl } from "react-intl";

import { assertNever } from "@/utils/never";

import styles from "./logo.module.css";

/** SdkMeet Logo Icon Part */
export const SdkMeetLogoPart: React.FC<React.ComponentProps<"svg">> = (props) => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 200 200"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <defs>
      <linearGradient id="sdk_grad_logo" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0EA5E9" />
        <stop offset="100%" stopColor="#2563EB" />
      </linearGradient>
      <linearGradient id="inner_grad_logo" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFFFFF" />
        <stop offset="100%" stopColor="#F1F5F9" />
      </linearGradient>
      <filter id="glow_logo" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="#1E40AF" floodOpacity="0.3" />
      </filter>
    </defs>
    <rect width="200" height="200" rx="52" fill="url(#sdk_grad_logo)" filter="url(#glow_logo)" />
    <path
      d="M52 74C52 57.4315 65.4315 44 82 44H118C134.569 44 148 57.4315 148 74V96C148 112.569 134.569 126 118 126H86L62 144V123.8C55.88 119.5 52 112.4 52 104V74Z"
      fill="url(#inner_grad_logo)"
    />
    <rect x="76" y="68" width="34" height="28" rx="6" fill="#2563EB" />
    <path d="M110 75L124 67V97L110 89V75Z" fill="#0EA5E9" />
    <circle cx="93" cy="82" r="5" fill="#38BDF8" />
    <circle cx="93" cy="82" r="2.2" fill="#FFFFFF" />
  </svg>
);

/** The horizontal SDKmeet logotype */
export const ElementLogotype: React.FC = () => {
  const intl = useIntl();
  const title = intl.formatMessage({
    id: "product.element",
    description: "SDKmeet brand name",
    defaultMessage: "SDKmeet",
  });

  return (
    <div
      role="img"
      aria-label={title}
      style={{ display: "inline-flex", alignItems: "center", gap: "8px", verticalAlign: "middle" }}
    >
      <SdkMeetLogoPart style={{ width: "22px", height: "22px" }} />
      <span style={{ fontWeight: 700, fontSize: "16px", letterSpacing: "-0.3px", color: "currentColor" }}>
        SDK<span style={{ color: "#0EA5E9" }}>meet</span>
      </span>
    </div>
  );
};

type Variant = "pro" | "community" | "ti-m";

interface ElementLogoProps extends React.ComponentProps<"div"> {
  variant: Variant;
}

/** The SDKmeet Admin logo in the header: icon + text + badge */
export const ElementLogo: React.FC<ElementLogoProps> = ({
  variant,
  ...props
}: ElementLogoProps) => {
  const intl = useIntl();
  let title = "SDKmeet Admin";
  let badgeLabel = "Admin";

  switch (variant) {
    case "pro": {
      title = intl.formatMessage({
        id: "product.title.pro",
        defaultMessage: "SDKmeet Enterprise",
        description: "The main name of the admin console, the Enterprise variant",
      });
      badgeLabel = "Enterprise";
      break;
    }
    case "community": {
      title = intl.formatMessage({
        id: "product.title.community",
        defaultMessage: "SDKmeet Admin",
        description: "The main name of the admin console, the Community variant",
      });
      badgeLabel = "Admin";
      break;
    }
    case "ti-m": {
      title = intl.formatMessage({
        id: "product.title.ti-m",
        defaultMessage: "SDKmeet Suite",
        description: "The main name of the admin console",
      });
      badgeLabel = "Suite";
      break;
    }
    default: {
      assertNever(variant);
    }
  }

  return (
    <div
      className={styles["element-logo-container"]}
      role="img"
      aria-label={title}
      data-variant={variant}
      style={{ display: "inline-flex", alignItems: "center", gap: "10px", cursor: "pointer" }}
      {...props}
    >
      <SdkMeetLogoPart style={{ width: "32px", height: "32px" }} aria-hidden="true" />
      <span
        style={{
          fontWeight: 800,
          fontSize: "18px",
          letterSpacing: "-0.4px",
          color: "var(--cpd-color-text-primary, currentColor)",
          userSelect: "none",
        }}
        aria-hidden="true"
      >
        SDK<span style={{ color: "#0EA5E9" }}>meet</span>
      </span>
      <div
        className={styles["badge"]}
        aria-hidden="true"
        style={{
          padding: "2px 8px",
          borderRadius: "6px",
          fontSize: "11px",
          fontWeight: 700,
          background: "linear-gradient(135deg, #0EA5E9, #2563EB)",
          color: "#ffffff",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          border: "none",
        }}
      >
        {badgeLabel}
      </div>
    </div>
  );
};

export const ProBadge: React.FC = () => {
  const intl = useIntl();
  const title = intl.formatMessage({
    id: "product.badge.pro",
    description: "Accessible label for Enterprise badge",
    defaultMessage: "Enterprise",
  });
  return (
    <div data-variant="pro">
      <div
        className={styles["badge"]}
        role="img"
        aria-label={title}
        style={{
          padding: "2px 8px",
          borderRadius: "6px",
          fontSize: "11px",
          fontWeight: 700,
          background: "linear-gradient(135deg, #0EA5E9, #2563EB)",
          color: "#ffffff",
        }}
      >
        Enterprise
      </div>
    </div>
  );
};

/** SDKmeet Admin vertical logo on auth/landing page */
export const EssLogotypeVertical: React.FC = () => {
  const intl = useIntl();
  const title = intl.formatMessage({
    id: "product.title",
    defaultMessage: "SDKmeet Admin",
    description: "The main name of the admin console",
  });

  return (
    <div
      role="img"
      aria-label={title}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "14px",
      }}
    >
      <SdkMeetLogoPart
        style={{
          width: "72px",
          height: "72px",
          filter: "drop-shadow(0 8px 24px rgba(14, 165, 233, 0.35))",
        }}
      />
      <div style={{ textAlign: "center" }}>
        <div
          style={{
            fontWeight: 800,
            fontSize: "24px",
            letterSpacing: "-0.5px",
            color: "var(--cpd-color-text-primary, currentColor)",
          }}
        >
          SDK<span style={{ color: "#0EA5E9" }}>meet</span>
        </div>
        <div
          style={{
            fontWeight: 600,
            fontSize: "12px",
            letterSpacing: "1.2px",
            textTransform: "uppercase",
            color: "var(--cpd-color-text-secondary, #64748b)",
            marginTop: "2px",
          }}
        >
          Admin Console
        </div>
      </div>
    </div>
  );
};
